import { errorMessage } from "@atlas/shared";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  UserMinus,
  CalendarPlus,
  Download,
  Pencil,
} from "lucide-react";
import {
  fetchAdminPartnerCodes,
  createPartnerCode,
  updatePartnerCode,
  fetchPartnerCodeMembers,
  removePartnerCodeMember,
  extendPartnerCodeMember,
  type PartnerCode,
  type PartnerCodeMember,
  type PartnerCodeInput,
} from "../../api/partners";
import { Button } from "../../components/Button";
import { AtlasPanel } from "../../components/AtlasPanel";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

// Partner codes (ATL-134): one row per creator / press / channel link with
// the signup funnel it produced (same stage definitions as the Funnel tab).
// Settlement with creators is by hand from this table and the CSV.

const CODES_KEY = ["admin/partners"];
const membersKey = (id: string) => ["admin/partners", id, "members"];

const TRIAL_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "No Pro trial (attribution only)" },
  { value: "10", label: "10 days of Pro" },
  { value: "30", label: "30 days of Pro" },
  { value: "90", label: "90 days of Pro" },
  { value: "365", label: "A year of Pro" },
];

function partnerUrl(code: string) {
  return `${window.location.origin}/with/${code}`;
}

function pct(n: number, d: number): string {
  if (d <= 0) return "";
  return `${Math.round((n / d) * 100)}%`;
}

function shortDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "2-digit" }) : "—";
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 36,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  height: 72,
  padding: "8px 10px",
  resize: "vertical",
  fontFamily: "inherit",
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 6,
};

const headerCellStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

function CopyLinkButton({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(partnerUrl(code)).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      title="Copy link"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        background: "none",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        cursor: "pointer",
        color: copied ? "var(--status-success)" : "var(--text-secondary)",
        padding: "4px 8px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied" : "Copy link"}
    </button>
  );
}

// ─── CSV ──────────────────────────────────────────────────────────────────────

function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(codes: PartnerCode[]) {
  const header = [
    "partner", "code", "link", "enabled", "pro_trial_days", "seat_cap", "seats_used",
    "signed_up", "source_connected", "activated", "pro", "last_signup", "created", "notes",
  ];
  const rows = codes.map((c) => [
    c.label, c.code, partnerUrl(c.code), c.enabled ? "yes" : "no", c.durationDays ?? "",
    c.maxSeats ?? "", c.seatsUsed, c.funnel.signedUp, c.funnel.sourceConnected,
    c.funnel.activated, c.funnel.pro, c.lastSignupAt ?? "", c.createdAt, c.notes,
  ]);
  const text = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `atlas-partners-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Dialogs ──────────────────────────────────────────────────────────────────

function DialogFrame({ title, onClose, children }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          width: 420,
          maxWidth: "100%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 20 }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

type FormState = {
  label: string;
  code: string;
  landingLine: string;
  notes: string;
  trial: string;
  maxSeats: string;
  fullHistory: boolean;
  /** Waypoints on the signup's first synced walk; blank = none. */
  reward: string;
  /** Edit only: the partner's own account (email or username). */
  owner: string;
};

function CodeFields({ form, setForm, editing }: {
  form: FormState;
  setForm: (f: FormState) => void;
  editing: boolean;
}) {
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <label style={fieldLabelStyle}>Partner name (shown on the page: "James sent you")</label>
        <input type="text" placeholder="e.g. James Forrest" value={form.label} onChange={set("label")} style={inputStyle} />
      </div>
      {!editing && (
        <div>
          <label style={fieldLabelStyle}>Code (optional, lowercase; random if blank)</label>
          <input type="text" placeholder="e.g. james" value={form.code} onChange={set("code")} style={{ ...inputStyle, fontFamily: "var(--font-mono)" }} />
        </div>
      )}
      <div>
        <label style={fieldLabelStyle}>Landing line (optional)</label>
        <input type="text" placeholder="e.g. Your Wainwrights, on the record." value={form.landingLine} onChange={set("landingLine")} style={inputStyle} />
      </div>
      <div>
        <label style={fieldLabelStyle}>Pro trial</label>
        <select value={form.trial} onChange={set("trial")} style={inputStyle}>
          {TRIAL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {form.trial && (
        <div>
          <label style={fieldLabelStyle}>Trial seat cap (optional, blank = no cap)</label>
          <input type="number" min={1} value={form.maxSeats} onChange={set("maxSeats")} style={inputStyle} />
        </div>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
        <input
          type="checkbox"
          checked={form.fullHistory}
          onChange={(e) => setForm({ ...form, fullHistory: e.target.checked })}
        />
        Grants Full history at signup (permanent; separate from the Pro trial)
      </label>
      <div>
        <label style={fieldLabelStyle}>Waypoints for each signup (paid when their first walk syncs; blank = none)</label>
        <input type="number" min={1} max={2000} step={50} placeholder="e.g. 500" value={form.reward} onChange={set("reward")} style={inputStyle} />
      </div>
      {editing && (
        <div>
          <label style={fieldLabelStyle}>Partner's Atlas account (email or username; they see their signups at /partner)</label>
          <input type="text" placeholder="e.g. telftreks@gmail.com" value={form.owner} onChange={set("owner")} style={inputStyle} />
        </div>
      )}
      <div>
        <label style={fieldLabelStyle}>Notes (rate agreed, where it was posted, settlement)</label>
        <textarea value={form.notes} onChange={set("notes")} style={textareaStyle} />
      </div>
    </div>
  );
}

function formToInput(form: FormState): PartnerCodeInput {
  const seats = parseInt(form.maxSeats, 10);
  return {
    label: form.label.trim(),
    ...(form.code.trim() ? { code: form.code.trim().toLowerCase() } : {}),
    landingLine: form.landingLine.trim(),
    notes: form.notes.trim(),
    durationDays: form.trial ? parseInt(form.trial, 10) : null,
    maxSeats: form.trial && Number.isInteger(seats) && seats >= 1 ? seats : null,
    grantsFullHistory: form.fullHistory,
    rewardWaypoints: parseReward(form.reward),
  };
}

function parseReward(raw: string): number | null {
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

function formValid(form: FormState): boolean {
  if (!form.label.trim()) return false;
  if (form.code && !/^[a-z0-9][a-z0-9-]{1,39}$/.test(form.code.trim().toLowerCase())) return false;
  if (form.trial && form.maxSeats && !(Number.isInteger(parseInt(form.maxSeats, 10)) && parseInt(form.maxSeats, 10) >= 1)) return false;
  if (form.reward.trim() && !/^\d+$/.test(form.reward.trim())) return false;
  if (form.reward.trim() && (parseInt(form.reward, 10) < 0 || parseInt(form.reward, 10) > 2000)) return false;
  return true;
}

const EMPTY_FORM: FormState = { label: "", code: "", landingLine: "", notes: "", trial: "", maxSeats: "", fullHistory: false, reward: "", owner: "" };

function CreateDialog({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (code: PartnerCode) => void;
}) {
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const mutation = useMutation({
    mutationFn: () => createPartnerCode(formToInput(form)),
    onSuccess: ({ code }) => onCreated(code),
  });

  return (
    <DialogFrame title="New partner code" onClose={onClose}>
      <CodeFields form={form} setForm={setForm} editing={false} />
      {mutation.isError && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 12 }}>
          {errorMessage(mutation.error, "Failed to create code")}
        </p>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={mutation.isPending || !formValid(form)} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Creating…" : "Create code"}
        </Button>
      </div>
    </DialogFrame>
  );
}

function EditDialog({ code, onClose }: { code: PartnerCode; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = React.useState<FormState>({
    label: code.label ?? "",
    code: code.code,
    landingLine: code.landingLine ?? "",
    notes: code.notes ?? "",
    trial: code.durationDays == null ? "" : String(code.durationDays),
    maxSeats: code.maxSeats == null ? "" : String(code.maxSeats),
    fullHistory: code.grantsFullHistory,
    reward: code.rewardWaypoints == null ? "" : String(code.rewardWaypoints),
    owner: code.owner?.email ?? code.owner?.username ?? "",
  });
  const initialOwner = code.owner?.email ?? code.owner?.username ?? "";
  const mutation = useMutation({
    mutationFn: () => {
      const input = formToInput(form);
      return updatePartnerCode(code.id, {
        label: input.label,
        landingLine: input.landingLine || null,
        notes: input.notes || null,
        durationDays: input.durationDays,
        maxSeats: input.maxSeats,
        grantsFullHistory: input.grantsFullHistory,
        rewardWaypoints: input.rewardWaypoints,
        // Only send the owner when it changed, so an unchanged form never re-resolves it.
        ...(form.owner.trim() !== initialOwner ? { owner: form.owner.trim() || null } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CODES_KEY });
      onClose();
    },
  });

  return (
    <DialogFrame title={`Edit ${code.label ?? code.code}`} onClose={onClose}>
      <CodeFields form={form} setForm={setForm} editing />
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 12 }}>
        Changing the trial only affects people who sign up from now on.
      </p>
      {mutation.isError && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 12 }}>
          {errorMessage(mutation.error, "Update failed")}
        </p>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={mutation.isPending || !formValid(form)} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </DialogFrame>
  );
}

function ExtendDialog({ member, onConfirm, onClose, pending, error }: {
  member: PartnerCodeMember;
  onConfirm: (days: number) => void;
  onClose: () => void;
  pending: boolean;
  error: Error | null;
}) {
  const [days, setDays] = React.useState("30");
  const valid = Number.isInteger(parseInt(days, 10)) && parseInt(days, 10) >= 1;

  return (
    <DialogFrame title="Extend Pro trial" onClose={onClose}>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 14 }}>
        Add days to {member.email}'s Pro access. Extensions add to their current expiry date.
      </p>
      <label style={fieldLabelStyle}>Days to add</label>
      <input type="number" value={days} onChange={(e) => setDays(e.target.value)} min={1} style={inputStyle} />
      {error && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 12 }}>{error.message}</p>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={pending || !valid} onClick={() => onConfirm(parseInt(days, 10))}>
          {pending ? "Extending…" : "Extend"}
        </Button>
      </div>
    </DialogFrame>
  );
}

// ─── Members panel ────────────────────────────────────────────────────────────

function memberStatus(m: PartnerCodeMember): { label: string; color: string } {
  if (m.hasSubscription) return { label: "Subscribed", color: "var(--status-success)" };
  const expired = m.planExpiresAt && new Date(m.planExpiresAt) <= new Date();
  if (m.plan === "pro" && !expired) return { label: "Pro", color: "var(--text-accent)" };
  if (m.seat?.removedAt) return { label: "Trial ended", color: "var(--text-muted)" };
  if (m.seat) return { label: "Trial expired", color: "var(--text-muted)" };
  return { label: "Free", color: "var(--text-muted)" };
}

function stageLabel(m: PartnerCodeMember): string {
  if (m.activated) return "Activated";
  if (m.sourceConnected) return "Source connected";
  return "Signed up";
}

function MembersPanel({ code }: { code: PartnerCode }) {
  const queryClient = useQueryClient();
  const [extending, setExtending] = React.useState<PartnerCodeMember | null>(null);

  const membersQuery = useQuery({
    queryKey: membersKey(code.id),
    queryFn: () => fetchPartnerCodeMembers(code.id),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: membersKey(code.id) });
    void queryClient.invalidateQueries({ queryKey: CODES_KEY });
  };

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removePartnerCodeMember(code.id, userId),
    onSuccess: invalidate,
  });

  const extendMutation = useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) =>
      extendPartnerCodeMember(code.id, userId, days),
    onSuccess: () => {
      invalidate();
      setExtending(null);
    },
  });

  const members = membersQuery.data?.members ?? [];

  if (membersQuery.isLoading) {
    return <div style={{ padding: "10px 0", color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>;
  }
  if (members.length === 0) {
    return (
      <div style={{ padding: "10px 0", color: "var(--text-muted)", fontSize: 13 }}>
        Nobody has signed up through this link yet.
      </div>
    );
  }

  const gridColumns = "minmax(0,2fr) 80px 120px 90px 90px auto";

  return (
    <div className="r-scroll-x">
      <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 12, padding: "6px 0", borderBottom: "1px solid var(--border-subtle)", minWidth: 640 }}>
        {["Person", "Signed up", "Stage", "Pro until", "Status", ""].map((h) => (
          <span key={h} style={headerCellStyle}>{h}</span>
        ))}
      </div>
      {members.map((m) => {
        const status = memberStatus(m);
        const activeSeat = !!m.seat && !m.seat.removedAt;
        return (
          <div
            key={m.userId}
            style={{
              display: "grid",
              gridTemplateColumns: gridColumns,
              alignItems: "center",
              gap: 12,
              padding: "9px 0",
              borderBottom: "1px solid var(--border-subtle)",
              minWidth: 640,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {m.email}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                {m.name ?? ""}{!m.attributed ? (m.name ? " · " : "") + "trial only, attributed elsewhere" : ""}
              </div>
            </div>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{shortDate(m.signedUpAt)}</span>
            <span style={{ fontSize: 12, color: m.activated ? "var(--status-success)" : "var(--text-muted)" }}>{stageLabel(m)}</span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {m.plan === "pro" ? shortDate(m.planExpiresAt) : "—"}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: status.color }}>{status.label}</span>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              {activeSeat && (
                <>
                  <Button variant="ghost" size="sm" leftIcon={<CalendarPlus size={12} />} onClick={() => setExtending(m)}>
                    Extend
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<UserMinus size={12} />}
                    disabled={removeMutation.isPending}
                    onClick={() => {
                      if (window.confirm(`End ${m.email}'s Pro trial from this link? Their signup still counts for the partner.`)) {
                        removeMutation.mutate(m.userId);
                      }
                    }}
                  >
                    End trial
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}

      {removeMutation.isError && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 10 }}>
          {errorMessage(removeMutation.error, "Failed to end the trial")}
        </p>
      )}

      {extending && (
        <ExtendDialog
          member={extending}
          pending={extendMutation.isPending}
          error={extendMutation.error instanceof Error ? extendMutation.error : null}
          onClose={() => setExtending(null)}
          onConfirm={(days) => extendMutation.mutate({ userId: extending.userId, days })}
        />
      )}
    </div>
  );
}

// ─── Code row ─────────────────────────────────────────────────────────────────

const ROW_COLUMNS = "16px minmax(0,2fr) 70px 90px 90px 70px 90px 80px auto";

function FunnelCell({ n, of }: { n: number; of: number }) {
  return (
    <span className="tnum" style={{ fontSize: 13, color: "var(--text-primary)" }}>
      {n}
      {of > 0 && n !== of && (
        <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 4 }}>{pct(n, of)}</span>
      )}
    </span>
  );
}

function CodeRow({ code }: { code: PartnerCode }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = React.useState(false);
  const [editing, setEditing] = React.useState(false);

  const updateMutation = useMutation({
    mutationFn: (data: { enabled: boolean }) => updatePartnerCode(code.id, data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CODES_KEY }),
  });

  const full = code.maxSeats != null && code.seatsUsed >= code.maxSeats;
  const trial =
    code.durationDays == null
      ? "none"
      : `${code.durationDays}d${code.maxSeats != null ? ` · ${code.seatsUsed}/${code.maxSeats}` : ` · ${code.seatsUsed}`}`;
  const trialLabel = [
    trial,
    code.grantsFullHistory ? "full history" : null,
    code.rewardWaypoints ? `${code.rewardWaypoints} WP` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: ROW_COLUMNS,
          alignItems: "center",
          gap: 12,
          padding: "12px 0",
          opacity: code.enabled ? 1 : 0.55,
          minWidth: 860,
        }}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? "Hide people" : "Show people"}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, display: "flex" }}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {code.label ?? "Unnamed"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 1 }}>
            /with/{code.code}
          </div>
        </div>

        <FunnelCell n={code.funnel.signedUp} of={0} />
        <FunnelCell n={code.funnel.sourceConnected} of={code.funnel.signedUp} />
        <FunnelCell n={code.funnel.activated} of={code.funnel.signedUp} />
        <FunnelCell n={code.funnel.pro} of={code.funnel.signedUp} />

        <span style={{ fontSize: 12, color: full ? "var(--status-warning)" : "var(--text-muted)" }} title="Pro trial · seats">
          {trialLabel}{full ? " full" : ""}
        </span>

        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{shortDate(code.lastSignupAt)}</span>

        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
          <button
            onClick={() => updateMutation.mutate({ enabled: !code.enabled })}
            disabled={updateMutation.isPending}
            style={{
              background: "none",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-pill)",
              cursor: "pointer",
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              color: code.enabled ? "var(--status-success)" : "var(--text-muted)",
            }}
          >
            {code.enabled ? "Live" : "Off"}
          </button>
          <Button variant="ghost" size="sm" leftIcon={<Pencil size={12} />} onClick={() => setEditing(true)}>
            Edit
          </Button>
          <CopyLinkButton code={code.code} />
        </div>
      </div>

      {code.owner && expanded && (
        <p style={{ fontSize: 12, color: "var(--text-muted)", padding: "0 0 4px 28px" }}>
          Partner account: {code.owner.email ?? `@${code.owner.username}`} (sees counts at /partner)
        </p>
      )}
      {code.notes && expanded && (
        <p style={{ fontSize: 12, color: "var(--text-muted)", padding: "0 0 8px 28px", whiteSpace: "pre-wrap" }}>{code.notes}</p>
      )}

      {updateMutation.isError && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", paddingBottom: 10 }}>
          {errorMessage(updateMutation.error, "Update failed")}
        </p>
      )}

      {expanded && (
        <div style={{ padding: "0 0 14px 28px" }}>
          <MembersPanel code={code} />
        </div>
      )}

      {editing && <EditDialog code={code} onClose={() => setEditing(false)} />}
    </div>
  );
}

// ─── AdminPartnersPage ────────────────────────────────────────────────────────

export default function AdminPartnersPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = React.useState(false);
  const [newCode, setNewCode] = React.useState<PartnerCode | null>(null);

  const codesQuery = useQuery({
    queryKey: CODES_KEY,
    queryFn: fetchAdminPartnerCodes,
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const codes = codesQuery.data?.codes ?? [];
  const totals = codes.reduce(
    (acc, c) => {
      acc.signedUp += c.funnel.signedUp;
      acc.activated += c.funnel.activated;
      acc.pro += c.funnel.pro;
      return acc;
    },
    { signedUp: 0, activated: 0, pro: 0 },
  );

  return (
    <AdminShell
      title="Partners"
      action={
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Download size={14} />}
            disabled={codes.length === 0}
            onClick={() => downloadCsv(codes)}
          >
            CSV
          </Button>
          <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setShowCreate(true)}>
            New code
          </Button>
        </div>
      }
    >
      {newCode && (
        <div style={{
          padding: "14px 16px",
          background: "var(--accent-soft)",
          border: "1px solid var(--border-gold)",
          borderRadius: "var(--radius-md)",
        }}>
          <div style={{ fontSize: 12, color: "var(--text-accent)", fontWeight: 600, marginBottom: 6 }}>
            Code created. Send this link to {newCode.label ?? "the partner"}:
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 700, color: "var(--text-primary)", wordBreak: "break-all" }}>
              {partnerUrl(newCode.code)}
            </span>
            <CopyLinkButton code={newCode.code} />
            <button
              onClick={() => setNewCode(null)}
              style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 18, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <AtlasPanel
        title={`Codes (${codes.length}) — ${totals.signedUp} signed up · ${totals.activated} activated · ${totals.pro} on Pro`}
      >
        {codesQuery.isLoading ? (
          <div style={{ padding: 16, color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>
        ) : codes.length === 0 ? (
          <div style={{ padding: "12px 0", color: "var(--text-muted)", fontSize: 13 }}>
            No partner codes yet. Create one per creator, publication or channel and share its link.
          </div>
        ) : (
          <div className="r-scroll-x">
            <div style={{ display: "grid", gridTemplateColumns: ROW_COLUMNS, gap: 12, padding: "6px 0", borderBottom: "1px solid var(--border-subtle)", minWidth: 860 }}>
              {["", "Partner", "Signed up", "Source", "Activated", "Pro", "Trial", "Last signup", ""].map((h, i) => (
                <span key={i} style={headerCellStyle}>{h}</span>
              ))}
            </div>
            {codes.map((code) => (
              <CodeRow key={code.id} code={code} />
            ))}
          </div>
        )}
      </AtlasPanel>

      <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
        Signed up counts accounts attributed to the code (staff and deleted accounts excluded).
        Source and Activated use the Funnel tab's definitions; Pro is the current plan from any source.
        Old /beta/ links keep working and count here too.
      </p>

      {showCreate && (
        <CreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={(code) => {
            setNewCode(code);
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: CODES_KEY });
            void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
          }}
        />
      )}
    </AdminShell>
  );
}
